<script lang="ts">
  /* eslint-disable import/order */
  import type { CheckboxProps } from '../ui/checkbox'
  import { computed, ref, useTemplateRef, type HTMLAttributes } from 'vue'

  export type GatCheckboxProps = Omit<CheckboxProps, 'modelValue'> & {
    /** The label of the input */
    label?: string
    showLabel?: boolean
    /** Description */
    description?: string
    /** The class of the input */
    class?: HTMLAttributes['class']
    /** Makes the input readonly */
    readonly?: boolean
    /** Show optional text */
    showOptional?: boolean
    /** Error messages */
    errorMessages?: string[]
    /** Hide details */
    hideDetails?: boolean | 'auto'
    /** Validate on */
    validateOn?: 'blur' | 'input' | 'manual'
    /** Validation rules */
    rules?:
      | FormFieldValidationRule<boolean | 'indeterminate'>
      | FormFieldValidationRule<boolean | 'indeterminate'>[]
    /** Display hint-message to the user */
    hint?: string
    /** Data testid */
    dataTestid?: string
    /** A tooltip hint that can be shown by clicking/hovering on the icon. */
    tooltipHint?: string
    /** The type of the field */
    fieldType?: 'default' | 'inset'
  }
</script>

<script setup lang="ts">
  import { Checkbox } from '../ui/checkbox'
  import { globalUtils } from '@gat-ship/utils'
  import { GatFormField } from '../gat-form-field'
  import type { FormFieldValidationRule } from '../gat-form-field/useFormField'
  import { TooltipHint } from '../tooltips'
  import FieldLabel from '../fields/FieldLabel.vue'
  const props = withDefaults(defineProps<GatCheckboxProps>(), {
    hideDetails: 'auto',
    showLabel: false,
    fieldType: undefined
  })

  const modelValue = defineModel<boolean | 'indeterminate' | undefined>({
    required: true
  })

  const inputLabel = computed(() => {
    if (!props.label) {
      return undefined
    }
    let result = props.label ?? ''
    if (props.readonly || props.showOptional === false) {
      return result
    }
    if (!props.required) {
      result += ` (optional)`
    }
    return result
  })

  const name = computed(() => {
    return (
      props.id ??
      props.label ??
      globalUtils.functions.generateShortUniqueId('input')
    )
  })

  const booleanValue = computed(() => {
    return modelValue.value === 'indeterminate'
      ? false
      : Boolean(modelValue.value)
  })

  const formField = useTemplateRef('form-field')

  async function validateField() {
    return await formField.value?.validate()
  }

  defineExpose({
    validate: validateField
  })
</script>

<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    :label="showLabel ? inputLabel : undefined"
    :tooltip-hint="showLabel ? tooltipHint : undefined"
    :name="name"
    :value="booleanValue"
    :field-type="fieldType"
    class="flex min-h-12 grow flex-col items-start justify-center"
  >
    <template #default="{ errorMessages, isValid }">
      <div
        class="flex cursor-pointer justify-self-center"
        :class="{
          'items-center': !props.description,
          'items-start': props.description
        }"
        @click.stop="modelValue = !modelValue"
        @keydown.space.enter.prevent="modelValue = !modelValue"
        :data-testid="dataTestid"
      >
        <div class="flex shrink-0 items-center justify-center p-2.5">
          <Checkbox v-model="modelValue" v-bind="props" :id="name" />
        </div>
        <div class="flex flex-col justify-center gap-1 pb-2.5">
          <FieldLabel
            v-if="!showLabel"
            :for="name"
            :class="['mt-[3px]', { 'text-error': errorMessages.length > 0 }]"
          >
            {{ inputLabel }}
            <TooltipHint v-if="props.tooltipHint" :text="props.tooltipHint" />
          </FieldLabel>
          <div v-if="description" class="text-soft whitespace-pre-line text-xs">
            {{ description }}
          </div>
        </div>
      </div>
    </template>
  </gat-form-field>
</template>
